import React from 'react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

function SortableItem({ id, renderItem, testIdPrefix }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  const handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      data-testid={`${testIdPrefix}-drag-handle-${id}`}
      aria-label="Drag to reorder"
      className="p-1 -ml-1 text-[#6b6b6b] hover:text-[#002b5c] cursor-grab active:cursor-grabbing touch-none"
    >
      <GripVertical size={16} />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'shadow-xl rounded-xl' : ''}>
      {renderItem(handle)}
    </div>
  );
}

export default function SortableClientGrid({
  rows, onReorder, renderItem, testIdPrefix = 'client', className = 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3',
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((r) => r.id === active.id);
    const to = rows.findIndex((r) => r.id === over.id);
    onReorder(arrayMove(rows, from, to));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={rows.map((r) => r.id)} strategy={rectSortingStrategy}>
        <div data-testid={`${testIdPrefix}-sortable-grid`} className={className}>
          {rows.map((r) => (
            <SortableItem key={r.id} id={r.id} testIdPrefix={testIdPrefix} renderItem={(handle) => renderItem(r, handle)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
